/**
 * POS printing — kitchen tickets and customer receipts sent straight to an
 * Epson TM-series printer (TM-T20IV) through its built-in ePOS-Print service.
 *
 * Same rail as the cage receipts (utils/receiptPrinter.js): the register's
 * browser POSTs ePOS-Print XML to https://<printerHost>/cgi-bin/epos/service.cgi.
 * The printer sits on a separate UniFi network, so the register must be allowed
 * through the firewall to the printer (TCP 443), and each register browser must
 * accept the printer's self-signed certificate once (open https://<printerHost>/).
 *
 * Settings (printerHost, which documents auto-print, receipt copies) live in the
 * `pos_printing` AppSetting — GET/PUT /api/v1/pos/printing, edited on POS → Menu.
 */
import api from '../api';
import { RECEIPT_BUSINESS } from './receiptPrinter';

const COLS = 42;

const TENDER_LABELS = { cash: 'Cash', chip_wallet: 'Player Bank', comp: 'Comp' };
const STATION_LABELS = { kitchen: 'KITCHEN', bar: 'BAR', none: 'OTHER' };
const DISCOUNT_LABELS = { player_comp: 'Player Comp', restaurant_discount: 'Discount', employee_meal: 'Employee Meal' };

const EPOS_FRIENDLY = {
  EX_ENPC: 'Could not reach the printer on the network.',
  EX_BADPORT: 'Could not open the printer port.',
  EX_TIMEOUT: 'The printer did not respond in time.',
  DeviceNotFound: 'The printer has no ePOS device with that Device ID. Check the Device ID in POS -> Menu -> Printer matches the printer\'s ePOS settings.',
  PrintSystemError: 'The printer reported a hardware error.',
  EPTR_COVER_OPEN: 'The printer cover is open.',
  EPTR_REC_EMPTY: 'The printer is out of paper.',
  EPTR_UNRECOVERABLE: 'The printer reported an unrecoverable error - power-cycle it.',
  EPTR_AUTOMATICAL: 'The printer reported a mechanical error (paper jam?).',
  SchemaError: 'The ticket data was rejected by the printer.',
  DeviceInUse: 'The printer is busy with another job. Try again.',
};

// ---------------------------------------------------------------- helpers
function asciiize(value) {
  return String(value == null ? '' : value)
    .normalize('NFKD')
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/[–—−]/g, '-')
    .replace(/[^\x20-\x7E]/g, '');
}
function xml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
function line(text) {
  return '<text>' + xml(asciiize(text)) + '&#10;</text>';
}
function padRow(left, right, cols = COLS) {
  const l = asciiize(left);
  const r = asciiize(right);
  if (l.length + r.length + 1 > cols) {
    const room = Math.max(0, cols - r.length - 1);
    return (l.slice(0, room) + ' ' + r).slice(0, cols);
  }
  return l + ' '.repeat(cols - l.length - r.length) + r;
}
function wrap(text, width) {
  const words = asciiize(text).split(/\s+/).filter(Boolean);
  const out = [];
  let cur = '';
  for (const w of words) {
    if (!cur) cur = w;
    else if ((cur + ' ' + w).length <= width) cur += ' ' + w;
    else { out.push(cur); cur = w; }
  }
  if (cur) out.push(cur);
  return out.length ? out : [''];
}
const rule = () => '<hline x1="0" x2="575" style="medium"/>';
const money = (c) => {
  const n = Number(c || 0);
  return (n < 0 ? '-$' : '$') + (Math.abs(n) / 100).toFixed(2);
};
function fmtTime(value) {
  const d = value ? new Date(value) : new Date();
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}
function customerLine(order) {
  const parts = [];
  if (order.customerName) parts.push(order.customerName);
  if (order.customerTable) parts.push('Table ' + order.customerTable);
  if (order.customerSeat) parts.push('Seat ' + order.customerSeat);
  return parts.join(' - ');
}
const liveLines = (order) => (order.lines || []).filter((l) => !l.voided);

// ------------------------------------------------------- kitchen ticket
/** ePOS commands for one kitchen ticket. Lines grouped by station, no prices. */
export function kitchenTicketCommands(order, location) {
  const lines = liveLines(order);
  const out = [];
  out.push('<text align="center"/>');
  out.push('<text reverse="true" em="true"/>');
  out.push(line(' ' + (location?.name ? asciiize(location.name).toUpperCase() + ' ' : '') + 'KITCHEN TICKET '));
  out.push('<text reverse="false" em="false"/>');
  out.push('<feed line="1"/>');
  out.push('<text width="3" height="3" em="true"/>');
  out.push(line('#' + (order.orderNumber ?? '')));
  out.push('<text width="1" height="1" em="false"/>');
  out.push(line(fmtTime(order.paidAt || order.createdAt)));
  const cust = customerLine(order);
  if (cust) {
    out.push('<text height="2" em="true"/>');
    for (const w of wrap(cust, COLS)) out.push(line(w));
    out.push('<text height="1" em="false"/>');
  }
  if (order.staffName) out.push(line('Rung by ' + order.staffName));
  out.push('<text align="left"/>');
  out.push(rule());

  const byStation = {};
  for (const l of lines) (byStation[l.station || 'kitchen'] ||= []).push(l);
  const stations = Object.keys(byStation).sort((a, b) =>
    ['kitchen', 'bar', 'none'].indexOf(a) - ['kitchen', 'bar', 'none'].indexOf(b));
  for (const st of stations) {
    if (stations.length > 1) {
      out.push('<text em="true"/>');
      out.push(line('-- ' + (STATION_LABELS[st] || st.toUpperCase()) + ' --'));
      out.push('<text em="false"/>');
    }
    for (const l of byStation[st]) {
      out.push('<text width="1" height="2" em="true"/>');
      for (const [i, w] of wrap(l.qty + ' x ' + l.nameSnapshot, COLS).entries()) {
        out.push(line(i === 0 ? w : '    ' + w));
      }
      out.push('<text width="1" height="1" em="false"/>');
      if (l.notes) for (const w of wrap('** ' + l.notes, COLS - 4)) out.push(line('    ' + w));
    }
  }
  if (order.notes) {
    out.push(rule());
    out.push('<text em="true"/>');
    for (const w of wrap('NOTE: ' + order.notes, COLS)) out.push(line(w));
    out.push('<text em="false"/>');
  }
  out.push('<feed line="2"/>');
  out.push('<cut type="feed"/>');
  return out.join('');
}

// ------------------------------------------------------- customer receipt
/** ePOS commands for one customer receipt. */
export function customerReceiptCommands(order, location, label) {
  const lines = liveLines(order);
  const out = [];
  out.push('<text align="center"/>');
  if (label) {
    out.push('<text reverse="true" em="true"/>');
    out.push(line(' ' + label + ' '));
    out.push('<text reverse="false" em="false"/>');
    out.push('<feed line="1"/>');
  }
  out.push('<text width="2" height="2" em="true"/>');
  out.push(line(location?.name || RECEIPT_BUSINESS.name));
  out.push('<text width="1" height="1" em="false"/>');
  if (location?.name) out.push(line(RECEIPT_BUSINESS.name));
  out.push(line(RECEIPT_BUSINESS.website));
  out.push('<feed line="1"/>');
  out.push('<text height="2" em="true"/>');
  out.push(line('Order #' + (order.orderNumber ?? '')));
  out.push('<text height="1" em="false"/>');
  out.push(line(fmtTime(order.paidAt || order.createdAt)));
  out.push('<text align="left"/>');
  out.push(rule());
  // Label/value rows; a value too long for one row goes on its own line(s).
  const kv = (label, value) => {
    if (asciiize(label).length + asciiize(value).length + 1 <= COLS) { out.push(line(padRow(label, value))); return; }
    out.push(line(label + ':'));
    for (const w of wrap(value, COLS - 2)) out.push(line('  ' + w));
  };
  if (order.staffName) kv('Cashier', order.staffName);
  const cust = customerLine(order);
  if (cust) kv('Guest', cust);
  if (order.staffName || cust) out.push(rule());

  for (const l of lines) {
    const amount = money(l.lineSubtotalCents);
    const label1 = l.qty + ' x ' + l.nameSnapshot;
    if (asciiize(label1).length + amount.length + 1 <= COLS) {
      out.push(line(padRow(label1, amount)));
    } else {
      const parts = wrap(label1, COLS - amount.length - 1);
      out.push(line(padRow(parts[0], amount)));
      for (const p of parts.slice(1)) out.push(line('    ' + p));
    }
    if (l.notes) for (const w of wrap(l.notes, COLS - 4)) out.push(line('    ' + w));
  }
  out.push(rule());
  out.push(line(padRow('Subtotal', money(order.subtotalCents))));
  if (order.discountCents) {
    const why = DISCOUNT_LABELS[order.discountType] || 'Discount';
    out.push(line(padRow(why + (order.discountReason ? ' (' + order.discountReason + ')' : ''), money(-order.discountCents))));
  }
  out.push(line(padRow('Tax', money(order.taxCents))));
  out.push('<text height="2" em="true"/>');
  out.push(line(padRow('TOTAL', money(order.totalCents))));
  out.push('<text height="1" em="false"/>');
  const tenders = (order.tenders || []).filter((t) => !t.voided);
  if (tenders.length) {
    out.push(rule());
    for (const t of tenders) {
      const name = TENDER_LABELS[t.type] || t.type;
      if (t.type === 'cash' && t.tenderedCents != null && t.tenderedCents !== t.amountCents) {
        out.push(line(padRow('Cash tendered', money(t.tenderedCents))));
        out.push(line(padRow('Change', money(t.changeCents))));
      } else {
        out.push(line(padRow(name, money(t.amountCents))));
      }
    }
  }
  out.push('<feed line="1"/>');
  out.push('<text align="center"/>');
  out.push(line('Thank you!'));
  out.push('<text align="left"/>');
  out.push('<feed line="2"/>');
  out.push('<cut type="feed"/>');
  return out.join('');
}

// ---------------------------------------------------------------- send
function envelope(commands) {
  return (
    '<?xml version="1.0" encoding="utf-8"?>' +
    '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>' +
    '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">' +
    commands +
    '</epos-print></s:Body></s:Envelope>'
  );
}

/** Turn the printer's ePOS-Print SOAP response into { ok } / { ok:false, code, error }. */
function parseEposResponse(text) {
  const success = /success="true"/i.test(text || '');
  const code = ((text || '').match(/code="([^"]*)"/i) || [])[1] || '';
  if (success) return { ok: true };
  return { ok: false, code, error: EPOS_FRIENDLY[code] || 'The printer rejected the job' + (code ? ' (' + code + ')' : '') + '.' };
}

/**
 * Send ePOS-Print commands. mode 'relay' (default): register -> backend -> TableCaptain bridge
 * (at the casino) -> printer, so no per-device certificate/network setup. If the relay itself
 * is unavailable, fall back to sending straight from this browser ('direct').
 */
export async function eposSend(printerHost, commands, deviceId, mode) {
  if (mode === 'direct') return eposSendDirect(printerHost, commands, deviceId);
  let relayError = null;
  try {
    const res = await api.post('/pos/print', { soap: envelope(commands) }, { timeout: 30000 });
    const d = res.data || {};
    if (d.printerStatus && d.printerStatus >= 400) return { ok: false, error: 'The printer returned HTTP ' + d.printerStatus + '.', via: 'relay' };
    return { ...parseEposResponse(d.printerBody), via: 'relay' };
  } catch (err) {
    const d = (err && err.response && err.response.data) || {};
    if (d.printerUnreachable) {
      return { ok: false, via: 'relay', error: 'The print relay could not reach the printer (' + (d.relayError || 'unreachable') + '). Is the printer on and connected?' };
    }
    if (err && err.response && err.response.status === 400) return { ok: false, via: 'relay', error: d.error || 'Print job rejected.' };
    relayError = d.relayError || d.error || (err && err.message) || 'relay unavailable';
  }
  console.warn('[POS print] relay unavailable, trying direct:', relayError);
  const direct = await eposSendDirect(printerHost, commands, deviceId);
  if (direct.ok) return { ...direct, via: 'direct' };
  return { ...direct, via: 'direct', error: 'Print relay unavailable (' + relayError + '). Direct print also failed: ' + direct.error };
}

/** POST ePOS-Print commands straight from this browser to the printer. */
async function eposSendDirect(printerHost, commands, deviceId) {
  const host = String(printerHost || '').trim();
  if (!host) return { ok: false, error: 'No POS printer IP is set (POS → Menu → Printer).' };
  // devid = the printer's ePOS "Device ID" (Epson default local_printer; ours is set in Web Config).
  const devid = encodeURIComponent(String(deviceId || 'local_printer').trim() || 'local_printer');
  const url = 'https://' + host + '/cgi-bin/epos/service.cgi?devid=' + devid + '&timeout=10000';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/xml; charset=utf-8',
        'If-Modified-Since': 'Thu, 01 Jan 1970 00:00:00 GMT',
        SOAPAction: '""',
      },
      body: envelope(commands),
      signal: controller.signal,
    });
    if (!res.ok) return { ok: false, error: 'The printer returned HTTP ' + res.status + '.' };
    const text = await res.text();
    const success = /success="true"/i.test(text);
    const code = (text.match(/code="([^"]*)"/i) || [])[1] || '';
    if (success) return { ok: true };
    return { ok: false, code, error: EPOS_FRIENDLY[code] || 'The printer rejected the job' + (code ? ' (' + code + ')' : '') + '.' };
  } catch (err) {
    if (err && err.name === 'AbortError') {
      return { ok: false, error: 'The printer at ' + host + ' did not respond. Is it on and reachable from this device?' };
    }
    // Diagnose: a no-cors GET only needs network + a trusted certificate (no CORS check).
    // If it succeeds, the connection is fine and the browser blocked the print request itself.
    let reachable = false;
    try {
      const probe = new AbortController();
      const pt = setTimeout(() => probe.abort(), 6000);
      await fetch('https://' + host + '/?probe=' + Date.now(), { mode: 'no-cors', cache: 'no-store', signal: probe.signal });
      clearTimeout(pt);
      reachable = true;
    } catch (_) { /* unreachable or untrusted certificate */ }
    console.warn('[POS print] request failed:', err && err.message, '| connection probe:', reachable ? 'OK' : 'FAILED');
    if (reachable) {
      return {
        ok: false,
        error: 'Connected to the printer at ' + host + ', but this browser blocked the print request (' +
          ((err && err.message) || 'request blocked') + '). Check this browser\'s local network / site permissions.',
      };
    }
    return {
      ok: false,
      error: 'Could not connect to the printer at ' + host + ' (network or certificate). On this device, open https://' + host +
        '/ - it must load without a certificate warning - and make sure this device is on the casino network.',
    };
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------- settings
export async function loadPosPrinting() {
  try {
    const res = await api.get('/pos/printing');
    return res.data || null;
  } catch (_) {
    return null;
  }
}

/**
 * Print what the settings call for when an order is paid/fired.
 * `which`: { kitchen?: bool, receipt?: bool } overrides the settings (used by reprint).
 * Kitchen ticket + receipt(s) go in ONE printer job, each with its own cut.
 */
export async function printPosOrder(order, settings, location, which) {
  if (!order || !settings) return { ok: false, error: 'Printing is not set up.' };
  const doKitchen = which ? !!which.kitchen : !!settings.kitchenTicket;
  const doReceipt = which ? !!which.receipt : !!settings.customerReceipt;
  let commands = '';
  if (doKitchen && liveLines(order).some((l) => (l.station || 'kitchen') !== 'none')) {
    commands += kitchenTicketCommands(order, location);
  }
  if (doReceipt) {
    const copies = Math.max(1, Math.min(3, parseInt(settings.receiptCopies, 10) || 1));
    for (let i = 0; i < copies; i += 1) {
      commands += customerReceiptCommands(order, location, which && which.reprint ? 'REPRINT' : (copies > 1 ? (i === 0 ? 'CUSTOMER COPY' : 'STORE COPY') : null));
    }
  }
  if (!commands) return { ok: true, skipped: true };
  return eposSend(settings.printerHost, commands, settings.deviceId, settings.mode);
}

/** A short test slip for the settings panel. */
export async function printPosTest(printerHost, location, deviceId, mode) {
  const out = [
    '<text align="center"/>',
    '<text width="2" height="2" em="true"/>',
    line('TEST PRINT'),
    '<text width="1" height="1" em="false"/>',
    line(location?.name || RECEIPT_BUSINESS.name),
    line(fmtTime()),
    line(mode === 'direct' ? 'Sent directly from this device.' : 'Sent via the print relay.'),
    '<feed line="2"/>',
    '<cut type="feed"/>',
  ].join('');
  return eposSend(printerHost, out, deviceId, mode);
}
